import { Eye, EyeOff } from 'lucide-react';
import { useId, useState, type ComponentProps, type ReactNode } from 'react';
import { cn } from '../lib/utils.js';
import { Input } from './input.js';

export function Label({ className, ...props }: ComponentProps<'label'>) {
  return (
    <label
      data-slot="label"
      className={cn('text-xs font-medium text-foreground', className)}
      {...props}
    />
  );
}

export interface FieldProps {
  readonly label: string;
  readonly hint?: ReactNode;
  /** Mensaje de validacion junto al campo; activa `aria-invalid`. */
  readonly error?: string | null | undefined;
  readonly className?: string;
  /** Recibe el id del control y los ids de ayuda/error para `aria-describedby`. */
  readonly children: (control: {
    readonly id: string;
    readonly 'aria-describedby'?: string;
    readonly 'aria-invalid'?: true;
  }) => ReactNode;
}

/**
 * Campo de formulario con etiqueta, ayuda y error accesibles.
 *
 * @example
 * <Field label="Servidor" error={errors.host}>{(control) => <Input {...control} />}</Field>
 */
export function Field({ label, hint, error, className, children }: FieldProps) {
  const id = useId();
  const hintId = hint === undefined ? undefined : `${id}-hint`;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ');
  return (
    <div data-slot="field" className={cn('flex flex-col gap-1', className)}>
      <Label htmlFor={id}>{label}</Label>
      {children({
        id,
        ...(describedBy === '' ? {} : { 'aria-describedby': describedBy }),
        ...(error ? { 'aria-invalid': true as const } : {}),
      })}
      {hint !== undefined && (
        <p id={hintId} className="text-[11px] text-muted-foreground">
          {hint}
        </p>
      )}
      {error ? (
        <p id={errorId} className="text-[11px] text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** Campo de contrasena con boton para mostrarla; nunca la autocompleta el navegador. */
export function PasswordInput({ className, ...props }: Omit<ComponentProps<'input'>, 'type'>) {
  const [visible, setVisible] = useState(false);
  return (
    <div className={cn('relative', className)}>
      <Input
        type={visible ? 'text' : 'password'}
        autoComplete="new-password"
        spellCheck={false}
        className="pr-8"
        {...props}
      />
      <button
        type="button"
        aria-label={visible ? 'Ocultar' : 'Mostrar'}
        className="absolute inset-y-0 right-0 flex w-8 items-center justify-center text-muted-foreground hover:text-foreground [&_svg]:size-3.5"
        onClick={() => {
          setVisible((value) => !value);
        }}
      >
        {visible ? <EyeOff aria-hidden /> : <Eye aria-hidden />}
      </button>
    </div>
  );
}
